import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ChevronRight, Package, RotateCcw } from 'lucide-react';
import { ShopChrome, SignInPrompt } from '@/components/shop/ShopChrome';
import { Pagination, StatusPill } from '@/components/shop/ShopUI';
import { EmptyState, Img, Shimmer } from '@/components/ui';
import { getShopOrders } from '@/api/shop';
import { useAuth } from '@/context/AuthContext';
import { friendlyError } from '@/lib/apiError';
import { ORDER_LABELS, SHIPMENT_LABELS, istDate, istDay, rupees, shipmentTone } from '@/lib/shop';

const orderTone = (s) => (s === 'cancelled' || s === 'expired' ? 'danger' : s === 'pending_payment' ? 'warning' : s === 'completed' ? 'success' : 'info');

/** One headline per order: the furthest-behind live shipment says where it is. */
const headline = (order) => {
  const ships = order.shipments || [];
  if (order.status === 'pending_payment') return { tone: 'warning', text: 'Payment pending' };
  if (!ships.length) return { tone: orderTone(order.status), text: ORDER_LABELS[order.status] || order.status };
  const live = ships.filter((s) => !['cancelled', 'returned_to_origin'].includes(s.status));
  if (!live.length) return { tone: 'danger', text: 'Cancelled' };
  const notDelivered = live.filter((s) => s.status !== 'delivered');
  if (!notDelivered.length) {
    const last = live.map((s) => s.deliveredAt).filter(Boolean).sort().pop();
    return { tone: 'success', text: last ? `Delivered on ${istDay(last)}` : 'Delivered' };
  }
  const s = notDelivered[0];
  const by = s.expectedBy ? ` · Arriving by ${istDay(s.expectedBy)}` : '';
  return { tone: shipmentTone(s.status), text: `${SHIPMENT_LABELS[s.status] || s.status}${by}` };
};

const ShopOrders = () => {
  const { user, openAuthModal } = useAuth();
  // The page lives in the URL (?page=), so back from an order returns to it.
  const [params, setParams] = useSearchParams();
  const page = Math.max(1, Number(params.get('page')) || 1);
  const setPage = (n) => {
    const next = new URLSearchParams(params);
    if (n > 1) next.set('page', String(n));
    else next.delete('page');
    setParams(next);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const [res, setRes] = useState(null);
  const [error, setError] = useState(null);
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    if (!user) return;
    let live = true;
    setError(null);
    getShopOrders(page)
      .then((r) => live && setRes(r))
      .catch((err) => live && setError(friendlyError(err, 'Your orders could not be loaded')));
    return () => {
      live = false;
    };
  }, [user, page, retry]);

  if (!user) {
    return (
      <ShopChrome>
        <SignInPrompt icon={Package} title="Your shop orders" message="Log in to track, cancel or return what you bought." onLogin={openAuthModal} />
      </ShopChrome>
    );
  }

  const orders = res?.data || [];

  return (
    <ShopChrome>
      <div className="lk-container max-w-4xl py-5 sm:py-8">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-h2 text-ink-900">My shop orders</h1>
            <p className="mt-1 text-sm text-ink-500">Track deliveries, cancel before dispatch and request returns.</p>
          </div>
          <Link to="/shop/returns" className="lk-btn-ghost min-h-[40px] px-4 py-2">
            <RotateCcw className="h-4 w-4" /> My returns
          </Link>
        </div>

        <div className="mt-5 space-y-3">
          {error ? (
            <EmptyState
              icon={Package}
              title="Something went wrong"
              message={error}
              action={
                <button type="button" onClick={() => setRetry((n) => n + 1)} className="lk-btn-primary">
                  Try again
                </button>
              }
            />
          ) : !res ? (
            Array.from({ length: 3 }, (_, i) => <Shimmer key={i} className="h-32 w-full rounded-2xl" />)
          ) : orders.length === 0 ? (
            <EmptyState icon={Package} title="No orders yet" message="Things you buy in the shop will show up here." action={<Link to="/shop" className="lk-btn-primary">Start shopping</Link>} />
          ) : (
            orders.map((o) => {
              const h = headline(o);
              const items = o.items || [];
              return (
                <Link key={o.id} to={`/shop/orders/${o.id}`} className="lk-card lk-focus group flex gap-4 p-4 transition-shadow hover:shadow-cardHover sm:p-5">
                  <div className="flex shrink-0 -space-x-6">
                    {items.slice(0, 3).map((i, idx) => (
                      <span key={i.id} className="relative" style={{ zIndex: 3 - idx }}>
                        <Img src={i.image} alt={i.title} className="h-20 w-16 rounded-xl ring-2 ring-white" />
                      </span>
                    ))}
                  </div>
                  <div className="min-w-0 flex-1">
                    <StatusPill tone={h.tone}>{h.text}</StatusPill>
                    <p className="mt-1.5 line-clamp-1 text-sm font-medium text-ink-900">
                      {items[0]?.title}
                      {items.length > 1 && <span className="text-ink-500"> + {items.length - 1} more</span>}
                    </p>
                    <p className="tabular mt-0.5 text-xs text-ink-500">
                      #{o.orderNo} · {istDate(o.createdAt)} · {rupees(o.total)}
                      {o.amountRefunded > 0 && <span className="text-success"> · {rupees(o.amountRefunded)} refunded</span>}
                    </p>
                  </div>
                  <ChevronRight className="h-5 w-5 shrink-0 self-center text-ink-400 transition-transform group-hover:translate-x-0.5" />
                </Link>
              );
            })
          )}
        </div>
        <Pagination page={page} lastPage={res?.pagination?.lastPage} onChange={setPage} />
      </div>
    </ShopChrome>
  );
};

export default ShopOrders;
